import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useRecentWikiArticles } from '@/hooks/useWikiArticles';
import { DELETION_KIND, REACTION_KIND, isLikeReaction } from '@/lib/nip25';

export interface StarredArticle {
  article: NostrEvent;
  starCount: number;
}

/**
 * Ranks a pool of recently-edited wiki articles by their NIP-25 "star"
 * count (see `useStars`/`StarButton`), for the "Most starred articles"
 * leaderboard page (`/stars`).
 *
 * NIP-54 has no built-in "trending" index to query directly, so ranking
 * requires counting `kind:7` reactions ourselves. To avoid one query per
 * article, this fetches a bounded candidate pool (the `poolSize` most
 * recently-edited articles across all authors) and then batches a
 * single reactions query across all of their event ids.
 */
export function useTopStarredArticles(limit = 24, poolSize = 150) {
  const { nostr } = useNostr();
  const { data: candidates, isLoading: candidatesLoading } = useRecentWikiArticles(poolSize);

  const ids = (candidates ?? []).map((a) => a.id);
  const idsKey = ids.join(',');

  const query = useQuery({
    queryKey: ['wiki', 'top-starred', idsKey, limit],
    queryFn: async (c) => {
      if (ids.length === 0) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);

      const reactionEvents = await nostr.query(
        [{ kinds: [REACTION_KIND], '#e': ids, limit: ids.length * 5 + 50 }],
        { signal },
      );
      const likes = reactionEvents.filter(isLikeReaction);

      // Honor NIP-09 deletion requests retracting a star ("unstarring"),
      // same as `useStars` does for a single article.
      let deletedIds = new Set<string>();
      if (likes.length > 0) {
        const deletions = await nostr.query(
          [{ kinds: [DELETION_KIND], '#e': likes.map((r) => r.id), limit: likes.length + 50 }],
          { signal },
        );
        deletedIds = new Set(
          likes
            .filter((r) =>
              deletions.some(
                (d) => d.pubkey === r.pubkey && d.tags.some(([n, v]) => n === 'e' && v === r.id),
              ),
            )
            .map((r) => r.id),
        );
      }

      const active = likes.filter((r) => !deletedIds.has(r.id));

      // Count distinct authors starring each target article — a single
      // author's duplicate/re-sent stars for the same article count once.
      const starsByTarget = new Map<string, Set<string>>();
      for (const reaction of active) {
        const targetId = reaction.tags.find(([n]) => n === 'e')?.[1];
        if (!targetId) continue;
        const authors = starsByTarget.get(targetId) ?? new Set<string>();
        authors.add(reaction.pubkey);
        starsByTarget.set(targetId, authors);
      }

      const ranked: StarredArticle[] = (candidates ?? []).map((article) => ({
        article,
        starCount: starsByTarget.get(article.id)?.size ?? 0,
      }));

      ranked.sort((a, b) => b.starCount - a.starCount || b.article.created_at - a.article.created_at);
      return ranked.slice(0, limit);
    },
    enabled: ids.length > 0,
    staleTime: 30_000,
  });

  return { ...query, isLoading: candidatesLoading || query.isLoading };
}
